import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, Loader2, Play, Clock, Info, Lightbulb, Volume2, Music, Image, Type, Sparkles, Hand, MousePointer2, Square, ChevronDown, ChevronUp, FileVideo } from 'lucide-react';

/**
 * 视频方案确认卡片 - 阶段二展示
 *
 * 按视频段落 + 分类明细展示方案，10s 倒计时自动开始生成（初次方案与调整后方案统一 10s），
 * 用户可手动确认或发送调整需求。
 */

export interface PlanTimelineItem {
  origin: 'user' | 'default';
  startSec: number;
  durationSec?: number;
  content: string;
  position?: string;
  size?: string;
  color?: string;
  animation?: string;
  trigger?: string;
  event?: string;
}

export interface VideoPlanSection {
  index: number;
  fileName: string;
  displayName?: string;
  duration: number;
  video: { playback: string; muted: boolean; origin: 'user' | 'default' };
  interaction: { mode: string; loopMode?: string; detail: string; origin: 'user' | 'default' };
  hotspots: PlanTimelineItem[];
  texts: PlanTimelineItem[];
  images: PlanTimelineItem[];
  particles: PlanTimelineItem[];
  sfx: PlanTimelineItem[];
  bgm: PlanTimelineItem[];
  sectionDefaults: string[];
}

export interface EndCardPlan {
  present: boolean;
  origin: 'user' | 'default';
  background: string;
  icon: string;
  cta: string;
  title: string;
  enter: string;
  hotspot: string;
  defaults: string[];
}

export interface VideoPlanConfirmation {
  videoCount: number;
  sections: VideoPlanSection[];
  endCard: EndCardPlan;
  global: string[];
  summary: string;
  layerRules?: string[];
}

interface VideoPlanConfirmCardProps {
  plan: VideoPlanConfirmation;
  onConfirm?: () => void;
  loading?: boolean;
  confirmed?: boolean;
  /** 倒计时秒数（默认 10，与后端方案卡 autoStartSec 统一） */
  autoStartSec?: number;
  /** BUG-20260908-PW-001: 管线已 running/done 时抑制自动倒计时触发（防刷新后自动重发确认） */
  suppressAutoStart?: boolean;
}

/** 分类图标标签映射 */
const CATEGORY_CONFIG: Array<{ key: keyof VideoPlanSection; icon: typeof Info; label: string }> = [
  { key: 'particles', icon: Sparkles, label: '粒子动画' },
  { key: 'texts', icon: Type, label: '文字信息' },
  { key: 'sfx', icon: Volume2, label: '音效' },
  { key: 'bgm', icon: Music, label: '背景音乐' },
  { key: 'images', icon: Image, label: '图片展示' },
  { key: 'hotspots', icon: MousePointer2, label: '事件热区' },
];

function PlanTimeline({ items, label, Icon }: { items: PlanTimelineItem[]; label: string; Icon: typeof Info }) {
  if (!items.length) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground/80">
        <Icon size={12} /> {label}
      </div>
      {items.map((item, i) => (
        <div key={i} className="ml-4 rounded-md border border-border/30 bg-card/60 px-2.5 py-1.5 text-xs leading-relaxed text-foreground/80">
          <div className="flex items-start justify-between gap-2">
            <span className="font-medium text-foreground">{item.content}</span>
            <span className={`shrink-0 rounded px-1 py-0.5 text-[10px] ${
              item.origin === 'user' ? 'bg-neon-cyan/10 text-neon-cyan' : 'bg-amber-500/10 text-amber-400'
            }`}>
              {item.origin === 'user' ? '用户设置' : '兜底'}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-foreground/60">
            <span>⏱ {item.startSec.toFixed(1)}s{item.durationSec !== undefined ? ` ~ ${(item.startSec + item.durationSec).toFixed(1)}s` : ' 起'}</span>
            {item.position && <span>📍 {item.position}</span>}
            {item.size && <span>📐 {item.size}</span>}
            {item.color && <span>🎨 {item.color}</span>}
            {item.trigger && <span>👆 {item.trigger}</span>}
            {item.event && <span>⚡ {item.event}</span>}
          </div>
          {item.animation && (
            <div className="mt-0.5 text-[11px] text-purple-400/80">
              <span className="font-medium">动画：</span>{item.animation}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SectionDefaults({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1 text-xs font-semibold text-amber-400/80">
        <Lightbulb size={11} /> 本段兜底说明
      </div>
      {items.map((d, i) => (
        <div key={i} className="ml-4 text-[11px] leading-relaxed text-amber-400/70">
          • {d}
        </div>
      ))}
    </div>
  );
}

export function VideoPlanConfirmCard({
  plan,
  onConfirm,
  loading = false,
  confirmed = false,
  autoStartSec = 10,
  suppressAutoStart = false,
}: VideoPlanConfirmCardProps): JSX.Element {
  const [expandedSection, setExpandedSection] = useState<number | null>(plan.sections[0]?.index ?? null);
  const [countdown, setCountdown] = useState(autoStartSec);
  const confirmedGuard = useRef(false);
  const onConfirmRef = useRef(onConfirm);
  onConfirmRef.current = onConfirm;
  const locked = confirmed || loading;
  // BUG-20260908-PW-001: 管线已 running/done（刷新/重挂载恢复）时不再自动倒计时触发——
  // planConfirmed 持久化前刷新会让卡片回到未确认态，若不抑制则自动重发「确认生成」
  const autoStartEnabled = !suppressAutoStart;

  // 倒计时自动确认（仅触发一次，防止 React 重渲染/StrictMode 重复调用；
  // 管线已 running/done 时不启动）
  useEffect(() => {
    if (!autoStartEnabled || locked || confirmed || confirmedGuard.current) return;
    if (countdown <= 0) {
      confirmedGuard.current = true;
      onConfirmRef.current?.();
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (!confirmedGuard.current) {
            confirmedGuard.current = true;
            onConfirmRef.current?.();
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [autoStartEnabled, locked, confirmed]);

  const toggleSection = (idx: number) => {
    setExpandedSection(expandedSection === idx ? null : idx);
  };

  const sectionItems = (s: VideoPlanSection) => CATEGORY_CONFIG.map(({ key, icon, label }) => {
    const items = s[key] as PlanTimelineItem[];
    if (!items?.length) return null;
    return <PlanTimeline key={key} items={items} label={label} Icon={icon} />;
  }).filter(Boolean);

  return (
    <div className="max-w-full space-y-3 rounded-2xl border border-neon-cyan/20 bg-[hsl(var(--accent)/0.04)] px-4 py-3">
      {/* 头部 */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neon-cyan/20">
            <FileVideo size={13} className="text-neon-cyan" />
          </div>
          <p className="text-sm font-semibold text-foreground">AI 互动方案</p>
        </div>
        <div className="flex items-center gap-2">
          {!locked && countdown > 0 && (
            <span className="flex items-center gap-1 rounded-full bg-neon-cyan/10 px-2.5 py-0.5 text-xs text-neon-cyan">
              <Clock size={11} /> {countdown}s
            </span>
          )}
          {confirmed ? (
            <span className="flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs text-green-400">
              <Check size={11} /> 已确认
            </span>
          ) : loading ? (
            <Loader2 size={14} className="animate-spin text-neon-cyan" />
          ) : null}
        </div>
      </div>

      {/* 一句话摘要 */}
      <p className="text-xs leading-relaxed text-foreground/70">{plan.summary}</p>

      {/* 全局项 */}
      {plan.global.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {plan.global.map((g, i) => (
            <span key={i} className="rounded-full bg-border/30 px-2 py-0.5 text-[10px] text-foreground/60">{g}</span>
          ))}
        </div>
      )}

      {/* 按视频段落展示 */}
      <div className="space-y-2">
        {plan.sections.map((s) => {
          const isExpanded = expandedSection === s.index;
          const items = sectionItems(s);
          const hasContent = items.length > 0 || s.sectionDefaults.length > 0;
          return (
            <div key={s.index} className="rounded-xl border border-border/30 bg-card/40">
              {/* 视频标题行 */}
              <button
                onClick={() => toggleSection(s.index)}
                className="flex w-full items-center justify-between px-3 py-2 text-left"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-neon-cyan/15 text-[10px] font-bold text-neon-cyan">
                    {s.index}
                  </span>
                  {/* DO-NOT-REVERT: BUG-20260917-VIDEO-PLANNAME 标题优先显示用户上传的原始文件名
                      （后端 sections[].displayName 填充），缺失时才回退「视频 N」——不得改回只显示「视频 N」 */}
                  <span className="text-xs font-semibold text-foreground">
                    {s.displayName || `视频 ${s.index}`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-foreground/50">{s.duration.toFixed(1)}s</span>
                  <span className="rounded bg-border/20 px-1.5 py-0.5 text-[10px] text-foreground/60">
                    {s.interaction.mode === 'freeze' ? '互动定格' : s.interaction.mode === 'loop' ? '互动循环' : '自动跳转'}
                  </span>
                  {isExpanded ? <ChevronUp size={12} className="text-foreground/50" /> : <ChevronDown size={12} className="text-foreground/50" />}
                </div>
              </button>

              {/* 展开内容 */}
              {isExpanded && hasContent && (
                <div className="space-y-2 border-t border-border/20 px-3 pb-3 pt-2">
                  {/* 视频描述 */}
                  <div className="flex items-center gap-1.5 text-[11px] text-foreground/70">
                    <span className="rounded bg-border/20 px-1.5 py-0.5 text-[10px]">{s.video.muted ? '静音' : '有声'}</span>
                    <span>{s.video.playback}</span>
                  </div>

                  {/* 互动元件 */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground/80">
                      <Square size={11} /> 互动元件
                    </div>
                    <div className="ml-4 rounded-md border border-border/30 bg-card/60 px-2.5 py-1.5 text-xs leading-relaxed text-foreground/80">
                      <span className={`rounded px-1 py-0.5 text-[10px] ${
                        s.interaction.origin === 'user' ? 'bg-neon-cyan/10 text-neon-cyan' : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        {s.interaction.origin === 'user' ? '用户设置' : '兜底'}
                      </span>
                      <span className="ml-1.5 text-foreground/80">{s.interaction.detail}</span>
                    </div>
                  </div>

                  {/* 按分类展示 */}
                  {items}

                  {/* 本段兜底说明 */}
                  <SectionDefaults items={s.sectionDefaults} />
                </div>
              )}
            </div>
          );
        })}

        {/* 结束页段落 */}
        {plan.endCard && (
          <div className="rounded-xl border border-border/30 bg-card/40">
            <button
              onClick={() => toggleSection(-1)}
              className="flex w-full items-center justify-between px-3 py-2 text-left"
            >
              <div className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-500/15 text-[10px] font-bold text-purple-400">
                  🏁
                </span>
                <span className="text-xs font-semibold text-foreground">结算页</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${
                  plan.endCard.origin === 'user' ? 'bg-neon-cyan/10 text-neon-cyan' : 'bg-amber-500/10 text-amber-400'
                }`}>{plan.endCard.origin === 'user' ? '用户设置' : '兜底'}</span>
                {expandedSection === -1 ? <ChevronUp size={12} className="text-foreground/50" /> : <ChevronDown size={12} className="text-foreground/50" />}
              </div>
            </button>
            {expandedSection === -1 && (
              <div className="space-y-1.5 border-t border-border/20 px-3 pb-3 pt-2">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] leading-relaxed text-foreground/80">
                  <span>🖼 背景：{plan.endCard.background}</span>
                  <span>🆔 图标：{plan.endCard.icon}</span>
                  <span>🔘 CTA：{plan.endCard.cta}</span>
                  <span>📝 标题：{plan.endCard.title}</span>
                  <span>🎬 入场：{plan.endCard.enter}</span>
                  <span>👆 热区：{plan.endCard.hotspot}</span>
                </div>
                {plan.endCard.defaults.length > 0 && <SectionDefaults items={plan.endCard.defaults} />}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 图层能力约束 */}
      {plan.layerRules && plan.layerRules.length > 0 && (
        <div className="rounded-lg border border-border/20 bg-card/30 px-3 py-2">
          <div className="flex items-center gap-1 text-[11px] font-semibold text-foreground/60">
            <Info size={10} /> 图层能力说明
          </div>
          {plan.layerRules.map((r, i) => (
            <div key={i} className="text-[10px] leading-relaxed text-foreground/50">• {r}</div>
          ))}
        </div>
      )}

      {/* 操作按钮 */}
      {!confirmed && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-[10px] leading-relaxed text-foreground/50">
            有任何需要调整的需求可以随时打断我，发送调整需求方案。
          </p>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex shrink-0 items-center gap-1.5 rounded-lg bg-neon-cyan px-3.5 py-2 text-xs font-semibold text-black transition-colors hover:bg-neon-cyan/80 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Play size={13} />
            )}
            {countdown > 0 ? `开始生成（${countdown}s）` : '开始生成'}
          </button>
        </div>
      )}
    </div>
  );
}